/**
 * shipping-estimator: カテゴリ別×国別送料の表示（Grailed風）data-shipping-estimator-v1
 *
 * データはスニペット内の <script type="application/json" data-se-rates>（shopメタフィールド由来）。
 * デフォルトゾーンの決め方（4段構え）:
 *   1. localStorage に保存された手動選択
 *   2. Shopify Markets の国（data-country。市場が複数ある時のみ信用する）
 *   3. ブラウザのタイムゾーンからの推定
 *   4. フォールバック: US
 */
if (!customElements.get('shipping-estimator')) {
  customElements.define(
    'shipping-estimator',
    class ShippingEstimator extends HTMLElement {
      static STORAGE_KEY = 'roamwalk:shipping-zone';
      // Grailed の並び順に合わせ、日本を末尾に追加
      static ZONE_ORDER = ['us', 'uk', 'asia', 'ca', 'eu', 'au', 'other', 'jp'];
      // タイムゾーン→カナダ判定用（America/ はデフォルト US）
      static CA_TIMEZONES = [
        'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg',
        'America/Halifax', 'America/St_Johns', 'America/Regina', 'America/Moncton',
        'America/Whitehorse', 'America/Yellowknife', 'America/Iqaluit',
      ];

      connectedCallback() {
        try {
          this.rates = JSON.parse(this.querySelector('[data-se-rates]').textContent);
        } catch (e) {
          this.hidden = true;
          return;
        }
        this.tierRates = (this.rates.tiers || {})[this.dataset.tier];
        if (!this.tierRates) {
          this.hidden = true;
          return;
        }
        this.labels = this.rates.labels || {};
        this.zones = ShippingEstimator.ZONE_ORDER.filter((z) => this.tierRates[z] != null);
        if (!this.zones.length) {
          this.hidden = true;
          return;
        }
        this.amountEl = this.querySelector('[data-se-amount]');
        this.zoneLabelEl = this.querySelector('[data-se-zone-label]');
        this.toggleBtn = this.querySelector('.shipping-estimator__toggle');
        this.listEl = this.querySelector('.shipping-estimator__list');

        this.buildList();
        this.select(this.defaultZone(), false);

        this.toggleBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
        // 外側クリック・Escで閉じる
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) this.close();
        });
        this.addEventListener('keyup', (e) => {
          if (e.key === 'Escape') {
            this.close();
            this.toggleBtn.focus();
          }
        });
      }

      defaultZone() {
        try {
          const saved = localStorage.getItem(ShippingEstimator.STORAGE_KEY);
          if (saved && this.zones.includes(saved)) return saved;
        } catch (e) {
          /* localStorage 不可（プライベートモード等）は無視 */
        }
        const multiMarket = parseInt(this.dataset.countryCount || '0', 10) > 1;
        const byCountry = (this.rates.country_zone || {})[this.dataset.country];
        if (multiMarket && byCountry && this.zones.includes(byCountry)) return byCountry;
        const byTimezone = this.zoneFromTimezone();
        if (byTimezone && this.zones.includes(byTimezone)) return byTimezone;
        const fallback = this.rates.default_zone || 'us';
        return this.zones.includes(fallback) ? fallback : this.zones[0];
      }

      zoneFromTimezone() {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
          if (tz === 'Asia/Tokyo') return 'jp';
          if (tz.startsWith('Australia/') || tz === 'Pacific/Auckland') return 'au';
          if (tz === 'Europe/London' || tz === 'Europe/Belfast') return 'uk';
          if (tz.startsWith('Europe/')) return 'eu';
          if (tz.startsWith('Asia/')) return 'asia';
          if (tz.startsWith('America/')) {
            return ShippingEstimator.CA_TIMEZONES.includes(tz) ? 'ca' : 'us';
          }
          return null;
        } catch (e) {
          return null;
        }
      }

      buildList() {
        this.zones.forEach((zone) => {
          const li = document.createElement('li');
          li.className = 'shipping-estimator__option';
          li.setAttribute('role', 'option');
          li.dataset.zone = zone;
          const label = document.createElement('span');
          label.textContent = this.labels[zone] || zone.toUpperCase();
          const price = document.createElement('span');
          price.className = 'shipping-estimator__option-price';
          price.textContent = `$${this.tierRates[zone]}`;
          li.append(label, price);
          li.addEventListener('click', () => {
            this.select(zone, true);
            this.close();
          });
          this.listEl.appendChild(li);
        });
      }

      select(zone, persist) {
        this.currentZone = zone;
        this.amountEl.textContent = this.tierRates[zone];
        this.zoneLabelEl.textContent = this.labels[zone] || zone.toUpperCase();
        this.listEl.querySelectorAll('[role="option"]').forEach((el) => {
          el.setAttribute('aria-selected', el.dataset.zone === zone ? 'true' : 'false');
        });
        if (persist) {
          try {
            localStorage.setItem(ShippingEstimator.STORAGE_KEY, zone);
          } catch (e) {
            /* 保存できなくても表示は成立する */
          }
        }
      }

      toggle() {
        if (this.listEl.hidden) {
          this.listEl.hidden = false;
          this.toggleBtn.setAttribute('aria-expanded', 'true');
        } else {
          this.close();
        }
      }

      close() {
        this.listEl.hidden = true;
        this.toggleBtn.setAttribute('aria-expanded', 'false');
      }
    }
  );
}
